import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Segment,
  Dimmer,
  Loader,
  Container,
  Message,
  Icon,
  Modal,
  Button,
  Image,
  List,
  Form,
  Grid,
  Dropdown,
  Input,
  Label,
} from 'semantic-ui-react';
import { stockyApi } from '../misc/StockyApi';
import { useAuth } from '../context/AuthContext';
import { useOrderSummary } from '../context/OrderSummaryContext';
import GenericTable from '../common/GenericTable';
import images from '../../images';
import { formatCurrency } from '../misc/PriceFormatter';
import {
  pageBodyStyle,
  pageFrameStyle,
  pageShellStyle,
  sectionTitleStyle,
  surfaceCardStyle,
} from '../misc/pageStyles';
import {
  canRetryOrderApplication,
  getApplicationErrorCopy,
  hasOrderApplicationIssue,
} from './orderApplicationUx';
import { decorateOrdersWithPackGroups } from './orderPackGrouping';
import ReportCsvExportModal from '../reports/ReportCsvExportModal';
import {
  normalizeOrderReportFilters,
  orderExportFields,
  orderInitialFilters,
  orderMarketplaceOptions,
  orderStatusOptions,
  resolveReportSelectValue,
  validateReportFilters,
} from '../reports/reportFilters';

  const mutedTextStyle = {
  color: '#6B7280',
  fontSize: 13,
  lineHeight: 1.5,
};

const compactFieldStyle = {
  marginBottom: 0,
};

const ordersTableTypographyStyle = {
  fontFamily: '"Trebuchet MS", "Segoe UI", sans-serif',
  color: '#111827',
};

const readStoredGenericTablePageSize = (fallback = 30) => {
  if (typeof window === 'undefined') return fallback;
  const saved = window.localStorage.getItem('genericTable.itemsPerPage') || window.localStorage.getItem('itemsPerPage');
  const parsed = saved ? parseInt(saved, 10) : null;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const Orders = () => {
  const [ordersRaw, setOrdersRaw] = useState([]);
  const [sortedOrders, setSortedOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [column, setColumn] = useState('orderDate');
  const [direction, setDirection] = useState('descending');
  const [selectedItems, setSelectedItems] = useState([]);
  const [pendingFilters, setPendingFilters] = useState(orderInitialFilters);
  const [filters, setFilters] = useState(orderInitialFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => readStoredGenericTablePageSize(30));
  const [total, setTotal] = useState(0);
  const [bulkPriceUpdateEnabled, setBulkPriceUpdateEnabled] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  const [itemsModalOpen, setItemsModalOpen] = useState(false);
  const [itemsModalOrder, setItemsModalOrder] = useState(null);
  const [orderFinancialsByOrderId, setOrderFinancialsByOrderId] = useState({});
  const [orderFinancialsLoading, setOrderFinancialsLoading] = useState(false);
  const [financialModalOrderId, setFinancialModalOrderId] = useState(null);
  const [applicationModalOrder, setApplicationModalOrder] = useState(null);
  const [applicationRetrying, setApplicationRetrying] = useState(false);
  const [applicationRetryError, setApplicationRetryError] = useState('');
  const [ordersRefreshVersion, setOrdersRefreshVersion] = useState(0);
  const [detailModalOrder, setDetailModalOrder] = useState(null);
  const autoReconcileRequestKeysRef = useRef(new Set());

  const Auth = useAuth();
  const { refreshSummary: refreshOrderSummary } = useOrderSummary();
  const accessToken = Auth.user?.accessToken || Auth.getUser()?.accessToken || '';
  const authUser = useMemo(() => (accessToken ? { accessToken } : null), [accessToken]);

  useEffect(() => {
    if (!authUser) return;
    stockyApi
      .getSyncControlsMe(authUser)
      .then((response) => {
        setBulkPriceUpdateEnabled(response?.data?.bulkPriceUpdateEnabled === true);
      })
      .catch((requestError) => {
        console.error('Error fetching sync controls for orders:', requestError);
      });
  }, [authUser]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const queryKey = useMemo(
    () =>
      JSON.stringify({
        page,
        pageSize,
        from: filters.from || '',
        to: filters.to || '',
        marketplace: filters.marketplace || '',
        status: filters.status || '',
        marketplaceId: filters.marketplaceId || '',
        buyer: filters.buyer || '',
        sku: filters.sku || '',
        refresh: ordersRefreshVersion,
      }),
    [page, pageSize, filters.from, filters.to, filters.marketplace, filters.status, filters.marketplaceId, filters.buyer, filters.sku, ordersRefreshVersion],
  );

  useEffect(() => {
    async function fetchOrders() {
      try {
        if (!authUser) {
          setOrdersRaw([]);
          setTotal(0);
          return;
        }
        const response = await stockyApi.getOrders(authUser, {
          ...filters,
          page,
          pageSize,
        });
        const payload = response.data;
        if (payload && Array.isArray(payload.data)) {
          setOrdersRaw(payload.data || []);
          setTotal(Number(payload.total || 0));
          setPageSize(Number(payload.pageSize || pageSize));
          setOrderFinancialsByOrderId((prev) => {
            const next = { ...prev };
            (payload.data || []).forEach((order) => {
              if (order?.financialSnapshot) {
                next[order.id] = order.financialSnapshot;
              }
            });
            return next;
          });
        } else {
          const all = Array.isArray(payload) ? payload : [];
          const safePageSize = Math.max(1, Number(pageSize || 30));
          const startIndex = Math.max(0, (page - 1) * safePageSize);
          const paginatedRows = all.slice(startIndex, startIndex + safePageSize);
          setOrdersRaw(paginatedRows);
          setTotal(all.length);
          setOrderFinancialsByOrderId((prev) => {
            const next = { ...prev };
            paginatedRows.forEach((order) => {
              if (order?.financialSnapshot) {
                next[order.id] = order.financialSnapshot;
              }
            });
            return next;
          });
        }
      } catch (err) {
        console.error('Error al cargar ordenes:', err);
        setError('No se pudieron cargar las ordenes.');
      } finally {
        setIsLoading(false);
      }
    }
    setIsLoading(true);
    fetchOrders();
  }, [authUser, queryKey]);

  const formatDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
  };

  const formatMoneyARS = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '';
    return formatCurrency(amount, { currency: 'ARS' });
  };

  const formatCompactMoneyARS = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '';
    return formatCurrency(amount, {
      currency: 'ARS',
      compact: true,
      stripZeroDecimals: true,
    });
  };

  const markOrderRead = useCallback(async (rowOrOrder) => {
    if (!authUser || !rowOrOrder?.id || !rowOrOrder?.isUnread) return;
    try {
      await stockyApi.markOrderRead(rowOrOrder.id, authUser);
      setOrdersRaw((prev) =>
        (prev || []).map((order) =>
          order.id === rowOrOrder.id
            ? { ...order, readAt: new Date().toISOString(), isUnread: false }
            : order,
        ),
      );
      if (detailModalOrder?.id === rowOrOrder.id) {
        setDetailModalOrder((prev) =>
          prev ? { ...prev, readAt: new Date().toISOString(), isUnread: false } : prev,
        );
      }
      refreshOrderSummary({ silent: true });
    } catch (requestError) {
      console.error('Error marking order as read:', requestError);
    }
  }, [authUser, detailModalOrder?.id, refreshOrderSummary]);

  const openOrderDetail = (row) => {
    setDetailModalOrder(row);
    markOrderRead(row);
  };

  const hasInternalCostLoaded = (snapshot) => snapshot?.hasInternalCost === true;

  const hasActiveBillingCooldown = (snapshot) => {
    const rawUntil = snapshot?.details?.billingRateLimitedUntil;
    if (!rawUntil) {
      return false;
    }

    const untilTs = new Date(rawUntil).getTime();
    return Number.isFinite(untilTs) && untilTs > Date.now();
  };

  const shouldRefreshFinancialSnapshot = (row, snapshot) => {
    if (!row || row.marketplace !== 'mercado_libre') {
      return false;
    }
    if (hasOrderApplicationIssue(row)) {
      return false;
    }
    if (!snapshot) {
      return true;
    }
    if (snapshot.status === 'READY') {
      return false;
    }
    if (snapshot.status === 'FAILED' || snapshot.status === 'UNAUTHORIZED') {
      return false;
    }
    if (hasActiveBillingCooldown(snapshot)) {
      return false;
    }
    return snapshot.status === 'PENDING' || snapshot.status === 'PARTIAL';
  };

  const getVariationName = (listing) => {
    if (!listing) return '';
    if (listing.variationName) return String(listing.variationName);

    if (Array.isArray(listing.variations) && listing.variations[0]?.name) {
      return String(listing.variations[0].name);
    }

    const combos = listing.variations?.[0]?.attribute_combinations;
    if (Array.isArray(combos) && combos.length) {
      const text = combos
        .map((c) => c?.value_name || c?.valueName || c?.value || '')
        .filter(Boolean)
        .join(' / ');
      return text || '';
    }

    return '';
  };

  const getThumbUrl = (orderItem) => {
    const listing = orderItem?.listing;
    const pics = listing?.pictures;
    const first = Array.isArray(pics) ? pics[0] : null;
    return first?.secure_url || first?.secureUrl || first?.src || first?.url || '';
  };

  const resolveItemLabel = (orderItem) => {
    const listing = orderItem?.listing;
    if (!listing) return orderItem?.title || orderItem?.sellerSku || 'Item sin vincular';

    const title = listing?.title || '';
    const variationName = getVariationName(listing);
    if (title && variationName) return `${title} - ${variationName}`;
    return title || 'Sin item';
  };

  const resolveItemLink = (orderItem) => {
    return orderItem?.listing?.link || orderItem?.listing?.permalink || '';
  };

  const openItemsModal = (row) => {
    setItemsModalOrder(row);
    setItemsModalOpen(true);
  };

  const openApplicationModal = (row) => {
    setApplicationRetryError('');
    setApplicationModalOrder(row);
  };

  const retryApplication = async () => {
    if (!applicationModalOrder || !authUser) return;
    setApplicationRetrying(true);
    setApplicationRetryError('');
    try {
      await stockyApi.retryOrderApplication(authUser, applicationModalOrder.id);
      setApplicationModalOrder(null);
      setOrdersRefreshVersion((value) => value + 1);
    } catch (requestError) {
      const message = requestError?.response?.data?.message || 'No pudimos reintentar el pedido. Revisa la causa y proba nuevamente.';
      setApplicationRetryError(Array.isArray(message) ? message.join(' ') : String(message));
    } finally {
      setApplicationRetrying(false);
    }
  };

  const applyFilters = () => {
    setFilters(normalizeOrderReportFilters(pendingFilters));
    setPage(1);
  };

  const clearFilters = () => {
    setPendingFilters(orderInitialFilters);
    setFilters(orderInitialFilters);
    setPage(1);
  };

  const handleFiltersSubmit = (e) => {
    e.preventDefault();
    applyFilters();
  };

  const getOrderLink = (row) => {
    if (!row?.marketplaceId) return '';
    if (row.marketplace === 'mercado_libre') {
      return `https://www.mercadolibre.com.ar/ventas/${row.marketplaceId}/detalle`;
    }
    return '';
  };

  const rows = useMemo(() => {
    return (ordersRaw || []).map((o) => {
      const items = Array.isArray(o.items) ? o.items : [];
      const firstItem = items[0] || null;
      const itemLabel = resolveItemLabel(firstItem);
      const link = resolveItemLink(firstItem);
      const thumb = getThumbUrl(firstItem);

      return {
        id: o.id,
        marketplace: o.marketplace || '',
        marketplaceId: o.marketplaceId || '',
        packId: o.packId || null,
        status: o.status || '',
        totalAmount: o.totalAmount ?? null,
        paidAmount: o.paidAmount ?? null,
        chargedAmount: o.chargedAmount ?? o.billing?.finalChargedAmount ?? o.paidAmount ?? o.totalAmount ?? null,
        orderDate: o.orderDate ?? null,
        readAt: o.readAt ?? null,
        isUnread: o.isUnread === true || !o.readAt,
        buyer: o.buyer || null,
        sellerAccount: o.sellerAccount || null,
        sellerId: o.sellerId || o.sellerAccount?.id || '',
        sellerLabel: o.sellerAccount?.nickname || o.sellerLabel || o.sellerAccount?.label || o.sellerAccount?.id || '',
        shippingAddress: o.shippingAddress || null,
        billing: o.billing || null,
        items,
        financialSnapshot: o.financialSnapshot || null,
        firstItemLabel: itemLabel,
        firstItemLink: link,
        firstItemThumb: thumb,
        itemsCount: items.length,
        applicationStatus: o.applicationStatus || 'APPLIED',
        applicationErrorCode: o.applicationErrorCode || null,
        applicationErrorDetails: o.applicationErrorDetails || null,
        retryDisposition: o.retryDisposition || 'NEVER',
      };
    });
  }, [ordersRaw]);

  useEffect(() => {
    const sortedByDateDesc = [...rows].sort((a, b) => {
      const aTs = new Date(a.orderDate || 0).getTime() || 0;
      const bTs = new Date(b.orderDate || 0).getTime() || 0;
      return bTs - aTs;
    });
    setSortedOrders(decorateOrdersWithPackGroups(sortedByDateDesc));
    setColumn('orderDate');
    setDirection('descending');
  }, [rows]);

  useEffect(() => {
    if (!authUser || bulkPriceUpdateEnabled !== true || rows.length === 0) {
      return;
    }

    const orderIdsToRefresh = rows
      .filter((row) => {
        const currentSnapshot = orderFinancialsByOrderId[row.id] || row.financialSnapshot || null;
        return shouldRefreshFinancialSnapshot(row, currentSnapshot);
      })
      .map((row) => row.id);

    if (orderIdsToRefresh.length === 0) {
      return;
    }

    const requestKey = `${queryKey}::${[...orderIdsToRefresh].sort().join(',')}`;
    if (autoReconcileRequestKeysRef.current.has(requestKey)) {
      return;
    }
    autoReconcileRequestKeysRef.current.add(requestKey);

    let cancelled = false;
    setOrderFinancialsLoading(true);

    stockyApi
      .reconcileOrderFinancials(
        {
          orderIds: orderIdsToRefresh,
        },
        authUser,
      )
      .then((response) => {
        if (cancelled) return;
        const nextRows = Array.isArray(response?.data?.data) ? response.data.data : [];
        setOrderFinancialsByOrderId((prev) => {
          const next = { ...prev };
          nextRows.forEach((row) => {
            next[row.orderId] = row;
          });
          return next;
        });
      })
      .catch((requestError) => {
        if (cancelled) return;
        console.error('Error reconciling order financials:', requestError);
      })
      .finally(() => {
        if (!cancelled) {
          setOrderFinancialsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [authUser, bulkPriceUpdateEnabled, queryKey, rows, orderFinancialsByOrderId]);

  const renderMarketplace = (value) => {
    const isMl = value === 'mercado_libre';
    const icon = isMl ? images.meliIcon : images.tnIcon;
    const label = isMl ? 'Mercado Libre' : 'Tienda Nube';

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <img src={icon} alt={label} style={{ width: 22, height: 22 }} />
        <span>{label}</span>
      </div>
    );
  };

  const renderFinancialChip = (snapshot, row) => {
    if (!snapshot) {
      return orderFinancialsLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#666' }}>
          <Icon name="spinner" loading />
          Calculando
        </div>
      ) : (
        <span style={{ color: '#777' }}>Sin snapshot</span>
      );
    }

    const receivedAmount = formatMoneyARS(snapshot.netPayoutAmount);
    const marginAmount = hasInternalCostLoaded(snapshot)
      ? formatCompactMoneyARS(snapshot.marginAmount)
      : 'No disponible';
    const compactReceivedAmount = formatCompactMoneyARS(snapshot.netPayoutAmount);

    return (
      <div
        style={{
          minWidth: 188,
          display: 'grid',
          gap: 7,
          padding: '10px 11px',
          borderRadius: 14,
          background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
          border: '1px solid #E5E7EB',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.8)',
          fontFamily: '"Trebuchet MS", "Segoe UI", sans-serif',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 10,
            whiteSpace: 'nowrap',
          }}
        >
          <div style={{ ...mutedTextStyle, fontSize: 11 }}>Recibis</div>
          <div style={{ fontSize: 19, lineHeight: 1.08, fontWeight: 800, color: '#111827', whiteSpace: 'nowrap' }}>
            {compactReceivedAmount}
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 10,
            whiteSpace: 'nowrap',
          }}
        >
          <div style={{ ...mutedTextStyle, fontSize: 11 }}>Ganancia</div>
          <div
            style={{
              fontSize: 14,
              lineHeight: 1.2,
              fontWeight: 700,
              color: hasInternalCostLoaded(snapshot) && Number(snapshot.marginAmount || 0) < 0 ? '#B42318' : '#15803D',
              whiteSpace: 'nowrap',
            }}
          >
            {marginAmount}
          </div>
        </div>
        <Button
          basic
          size="mini"
          style={{ justifySelf: 'start', borderRadius: 10, fontWeight: 600, fontFamily: '"Trebuchet MS", "Segoe UI", sans-serif' }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            markOrderRead(row);
            setFinancialModalOrderId(row.id);
          }}
        >
          Ver detalle
        </Button>
      </div>
    );
  };

  const columnsConfig = useMemo(
    () => [
      {
        key: 'marketplace',
        label: 'Canal',
        width: '170px',
        render: (value) => renderMarketplace(value),
      },
      {
        key: 'marketplaceId',
        label: 'ID',
        width: '190px',
        render: (value, row) => {
          const link = getOrderLink(row);
          const orderId = link ? (
            <a
              href={link}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => {
                e.stopPropagation();
                markOrderRead(row);
              }}
              style={{ color: '#2563EB', fontWeight: row.isUnread ? 800 : 600, wordBreak: 'break-word' }}
            >
              {value}
            </a>
          ) : value;

          return row.packGroup ? (
            <span
              title="Esta orden forma parte de una compra agrupada"
              style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 6 }}
            >
              <Icon name="linkify" color="blue" style={{ margin: '2px 0 0' }} />
              {orderId}
            </span>
          ) : orderId;
        },
      },
      {
        key: 'sellerAccount',
        label: 'Cuenta',
        width: '180px',
        render: (_, row) => {
          const label = row.sellerLabel || row.sellerId;
          if (!label) {
            return <span style={{ color: '#777' }}>No disponible</span>;
          }
          return (
            <div style={{ display: 'grid', gap: 3 }}>
              <span style={{ fontWeight: row.isUnread ? 800 : 700, color: '#111827' }}>{label}</span>
              {row.sellerId && row.sellerId !== label ? (
                <span style={{ color: '#6B7280', fontSize: 12 }}>{row.sellerId}</span>
              ) : null}
            </div>
          );
        },
      },
      {
        key: 'item',
        label: 'Item',
        width: '440px',
        sortable: false,
        render: (_, row) => {
          const hasLink = !!row.firstItemLink;
          const thumb = row.firstItemThumb;

          const openModalIfMulti = (e) => {
            e.preventDefault();
            e.stopPropagation();
            openItemsModal(row);
          };

          return (
            <div
              style={{ display: 'flex', alignItems: 'flex-start', gap: 12, minWidth: 0, cursor: row.itemsCount > 1 ? 'pointer' : 'default' }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (row.itemsCount > 1) openItemsModal(row);
              }}
            >
              {thumb ? (
                <Image
                  src={thumb}
                  avatar
                  circular
                  style={{ width: 28, height: 28, objectFit: 'cover' }}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <div
                  style={{
                    width: 28,
                    height: 28,
                    minWidth: 28,
                    borderRadius: '50%',
                    background: '#eee',
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              )}

              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, gap: 4 }}>
                <div style={{ overflowWrap: 'anywhere', color: '#111827', fontWeight: 600, lineHeight: 1.35 }}>
                  {hasLink ? (
                    <a
                      href={row.firstItemLink}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      title={row.firstItemLabel}
                      style={{ color: '#2563EB', fontWeight: 600 }}
                    >
                      {row.firstItemLabel}
                    </a>
                  ) : (
                    <span title={row.firstItemLabel}>{row.firstItemLabel}</span>
                  )}
                </div>
                {row.itemsCount > 1 ? (
                  <a href="#items" onClick={openModalIfMulti} style={{ fontSize: 12, color: '#6B7280', fontWeight: 600 }}>
                    + {row.itemsCount - 1} items mas
                  </a>
                ) : null}
              </div>
            </div>
          );
        },
      },
      {
        key: 'status',
        label: 'Estado',
        width: '190px',
        render: (value, row) => {
          const hasApplicationIssue = hasOrderApplicationIssue(row);
          return (
            <div style={{ display: 'grid', gap: 7, justifyItems: 'start' }}>
              <Label
                size="small"
                style={{
                  borderRadius: 999,
                  padding: '8px 12px',
                  fontWeight: 700,
                  background: '#F3F4F6',
                  color: '#111827',
                }}
              >
                {value}
              </Label>
              {hasApplicationIssue ? (
                <Button
                  basic
                  color="red"
                  size="mini"
                  icon
                  labelPosition="left"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    openApplicationModal(row);
                  }}
                  style={{ borderRadius: 999, fontWeight: 700 }}
                >
                  <Icon name="warning sign" />
                  {row.applicationStatus === 'PARTIALLY_APPLIED' ? 'Aplicacion parcial' : 'Requiere atencion'}
                </Button>
              ) : null}
            </div>
          );
        },
      },
      {
        key: 'totalAmount',
        label: 'Total',
        width: '150px',
        render: (value) => formatMoneyARS(value),
      },
      {
        key: 'orderDate',
        label: 'Fecha',
        width: '190px',
        accessor: (row) => {
          const d = new Date(row.orderDate);
          return Number.isNaN(d.getTime()) ? 0 : d.getTime();
        },
        render: (_, row) => formatDateTime(row.orderDate),
      },
      ...(bulkPriceUpdateEnabled === true
        ? [
            {
              key: 'profitability',
              label: 'Rentabilidad',
              width: '240px',
              sortable: false,
              render: (_, row) => renderFinancialChip(orderFinancialsByOrderId[row.id], row),
            },
          ]
        : []),
    ],
    [bulkPriceUpdateEnabled, markOrderRead, orderFinancialsByOrderId, orderFinancialsLoading],
  );

  const getSortValue = (row, colKey) => {
    const col = columnsConfig.find((c) => c.key === colKey);
    if (!col) return row?.[colKey];
    if (typeof col.accessor === 'function') return col.accessor(row);
    return row?.[colKey];
  };

  const handleSort = (clickedColumn) => {
    const colDef = columnsConfig.find((c) => c.key === clickedColumn);
    if (colDef?.sortable === false) return;

    if (column !== clickedColumn) {
      const sorted = [...rows].sort((a, b) => {
        const av = getSortValue(a, clickedColumn);
        const bv = getSortValue(b, clickedColumn);

        const aN = av === null || av === undefined ? '' : av;
        const bN = bv === null || bv === undefined ? '' : bv;

        if (typeof aN === 'number' && typeof bN === 'number') return aN - bN;
        return String(aN).localeCompare(String(bN));
      });

      setSortedOrders(sorted);
      setColumn(clickedColumn);
      setDirection('ascending');
    } else {
      setSortedOrders((prev) => [...prev].reverse());
      setDirection((prev) => (prev === 'ascending' ? 'descending' : 'ascending'));
    }
  };

  const orderActions = [
    {
      icon: 'eye',
      color: 'purple',
      title: 'Ver detalle',
      onClick: (row) => {
        openOrderDetail(row);
      },
    },
    {
      icon: 'truck',
      color: 'blue',
      title: 'Realizar envio',
      onClick: (row) => {
        alert(
          `Orden ${row.marketplaceId}: Para realizar envios, contacte soporte para habilitar esta funcionalidad.`,
        );
      },
    },
    {
      icon: 'file invoice',
      color: 'orange',
      title: 'Facturar',
      onClick: (row) => {
        alert(
          `Orden ${row.marketplaceId}: Para emitir facturas, contacte soporte para habilitar esta funcionalidad.`,
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <div className="centered-loader-container">
        <Dimmer active inverted>
          <Loader inverted size="huge">
            Cargando ordenes...
          </Loader>
        </Dimmer>
      </div>
    );
  }

  if (error) {
    return (
      <Container style={pageShellStyle}>
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      </Container>
    );
  }

  return (
    <Container fluid style={pageShellStyle}>
      <Segment style={pageFrameStyle}>
        <div style={pageBodyStyle}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => setFiltersExpanded((prev) => !prev)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setFiltersExpanded((prev) => !prev);
              }
            }}
            style={{
              ...sectionTitleStyle,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              cursor: 'pointer',
              marginBottom: filtersExpanded ? 14 : 18,
            }}
          >
            <Icon name={filtersExpanded ? 'chevron up' : 'chevron down'} style={{ margin: 0, color: '#6B7280' }} />
            <span>Filtros</span>
          </div>
          {filtersExpanded ? (
          <Segment style={{ ...surfaceCardStyle, padding: 18, marginBottom: 18 }}>
          <Form onSubmit={handleFiltersSubmit}>
            <Grid stackable columns={4}>
              <Grid.Row>
                <Grid.Column>
                  <Form.Input
                    label="Desde"
                    type="datetime-local"
                    style={compactFieldStyle}
                    value={pendingFilters.from}
                    onChange={(e) => setPendingFilters((prev) => ({ ...prev, from: e.target.value }))}
                  />
                </Grid.Column>
                <Grid.Column>
                  <Form.Input
                    label="Hasta"
                    type="datetime-local"
                    style={compactFieldStyle}
                    value={pendingFilters.to}
                    onChange={(e) => setPendingFilters((prev) => ({ ...prev, to: e.target.value }))}
                  />
                </Grid.Column>
                <Grid.Column>
                  <Form.Field style={compactFieldStyle}>
                    <label>Canal</label>
                    <Dropdown
                      selection
                      options={orderMarketplaceOptions}
                      value={resolveReportSelectValue(pendingFilters.marketplace, orderMarketplaceOptions)}
                      onChange={(_, data) =>
                        setPendingFilters((prev) => ({ ...prev, marketplace: String(data.value || '') }))
                      }
                    />
                  </Form.Field>
                </Grid.Column>
                <Grid.Column>
                  <Form.Field style={compactFieldStyle}>
                    <label>Estado</label>
                    <Dropdown
                      selection
                      options={orderStatusOptions}
                      value={resolveReportSelectValue(pendingFilters.status, orderStatusOptions)}
                      onChange={(_, data) =>
                        setPendingFilters((prev) => ({ ...prev, status: String(data.value || '') }))
                      }
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row verticalAlign="bottom">
                <Grid.Column>
                  <Form.Field style={compactFieldStyle}>
                    <label>ID Marketplace</label>
                    <Input
                      value={pendingFilters.marketplaceId}
                      onChange={(e) => setPendingFilters((prev) => ({ ...prev, marketplaceId: e.target.value }))}
                    />
                  </Form.Field>
                </Grid.Column>
                <Grid.Column>
                  <Form.Field style={compactFieldStyle}>
                    <label>SKU</label>
                    <Input
                      value={pendingFilters.sku}
                      onChange={(e) => setPendingFilters((prev) => ({ ...prev, sku: e.target.value }))}
                    />
                  </Form.Field>
                </Grid.Column>
                <Grid.Column>
                  <Form.Field style={compactFieldStyle}>
                    <label>Comprador</label>
                    <Input
                      value={pendingFilters.buyer}
                      onChange={(e) => setPendingFilters((prev) => ({ ...prev, buyer: e.target.value }))}
                    />
                  </Form.Field>
                </Grid.Column>
                <Grid.Column>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      gap: 10,
                      marginTop: 10,
                    }}
                  >
                    <Button basic type="button" onClick={clearFilters}>
                      Limpiar
                    </Button>
                    <Button color="purple" type="submit">
                      Aplicar filtros
                    </Button>
                  </div>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form>
          </Segment>
          ) : null}

          <div style={{ ...sectionTitleStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span>Listado</span>
            <Button color="purple" icon labelPosition="left" onClick={() => setExportModalOpen(true)}>
              <Icon name="download" />
              Exportar CSV
            </Button>
          </div>
          <Segment style={{ ...surfaceCardStyle, padding: 18 }}>
            <GenericTable
              data={sortedOrders}
              column={column}
              direction={direction}
              onSort={handleSort}
              columnsConfig={columnsConfig}
              showCheckbox={true}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              getRowId={(row) => row.id}
              actions={orderActions}
              emptyMessage="No hay ordenes para mostrar"
              serverPagination
              serverPage={page}
              serverTotalPages={totalPages}
              serverTotalItems={total}
              serverPageSize={pageSize}
              onServerPageChange={setPage}
              onServerPageSizeChange={(nextSize) => {
                setPageSize(nextSize);
                setPage(1);
              }}
              tableMinWidth={bulkPriceUpdateEnabled === true ? 1700 : 1440}
              tableStyle={ordersTableTypographyStyle}
              tableContainerStyle={{ borderRadius: 18, WebkitOverflowScrolling: "touch" }}
              getRowStyle={(row) => ({
                fontWeight: row.isUnread ? 800 : 400,
                background: row.isUnread ? '#FFF7ED' : undefined,
                borderLeft: row.packGroup ? '3px solid #7DD3FC' : undefined,
                borderTop: row.packGroup?.isFirst ? '3px solid #7DD3FC' : undefined,
                borderBottom: row.packGroup ? '1px solid #BAE6FD' : undefined,
              })}
            />
          </Segment>
        </div>
      </Segment>

      <ReportCsvExportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        title="Exportar ordenes"
        description="Se exportara una fila por item de orden, repitiendo los datos de la orden para facilitar la conciliacion. El filtro SKU conserva la misma coincidencia parcial del listado."
        initialFilters={filters}
        fields={orderExportFields}
        normalizeFilters={normalizeOrderReportFilters}
        validateFilters={(values) => validateReportFilters(values)}
        requestExport={(values, signal) => stockyApi.exportOrdersCsv(authUser, values, signal)}
        fallbackFilename="stocky-ordenes.csv"
      />

      <Modal open={itemsModalOpen} onClose={() => setItemsModalOpen(false)} size="small" closeIcon>
        <Modal.Header>Items de la orden</Modal.Header>
        <Modal.Content>
          {!itemsModalOrder ? (
            <div>Sin datos</div>
          ) : (
            <List divided relaxed>
              {(itemsModalOrder.items || []).map((it) => {
                const label = resolveItemLabel(it);
                const link = resolveItemLink(it);
                const thumb = getThumbUrl(it);

                return (
                  <List.Item key={it.id}>
                    <Image
                      src={thumb || undefined}
                      avatar
                      circular
                      style={{ width: 32, height: 32, objectFit: 'cover' }}
                    />
                    <List.Content>
                      <List.Header>
                        {link ? (
                          <a href={link} target="_blank" rel="noreferrer">
                            {label}
                          </a>
                        ) : (
                          label
                        )}
                      </List.Header>
                      <List.Description>
                        Cantidad: <b>{it.quantity}</b> | Unit: <b>{formatMoneyARS(it.unitPrice)}</b> | Subtotal:{' '}
                        <b>{formatMoneyARS(it.subtotal)}</b>
                      </List.Description>
                    </List.Content>
                  </List.Item>
                );
              })}
            </List>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setItemsModalOpen(false)}>Cerrar</Button>
        </Modal.Actions>
      </Modal>

      <Modal
        open={!!applicationModalOrder}
        onClose={() => !applicationRetrying && setApplicationModalOrder(null)}
        size="small"
        closeIcon={!applicationRetrying}
      >
        <Modal.Header>Este pedido necesita revision</Modal.Header>
        <Modal.Content>
          {applicationModalOrder ? (() => {
            const copy = getApplicationErrorCopy(applicationModalOrder.applicationErrorCode);
            const blockedItems = (applicationModalOrder.items || []).filter((item) => item.applicationStatus === 'BLOCKED');
            const canRetry = canRetryOrderApplication(applicationModalOrder);
            return (
              <div style={{ display: 'grid', gap: 18 }}>
                <Message warning icon>
                  <Icon name="warning sign" />
                  <Message.Content>
                    <Message.Header>{copy.title}</Message.Header>
                    <p>{copy.explanation}</p>
                  </Message.Content>
                </Message>
                <div>
                  <div style={{ fontWeight: 800, marginBottom: 6 }}>Que hacer</div>
                  <div style={mutedTextStyle}>{copy.nextStep}</div>
                </div>
                {blockedItems.length > 0 ? (
                  <div>
                    <div style={{ fontWeight: 800, marginBottom: 6 }}>
                      {blockedItems.length > 1 ? 'Productos afectados' : 'Producto afectado'}
                    </div>
                    <List divided relaxed>
                      {blockedItems.map((item) => (
                        <List.Item key={item.id || item.marketplaceItemId}>
                          <Icon name="exclamation circle" color="red" />
                          <List.Content>
                            <List.Header>{resolveItemLabel(item)}</List.Header>
                            <List.Description>
                              SKU: {item.sellerSku || 'sin SKU'} · Cantidad: {item.quantity}
                            </List.Description>
                          </List.Content>
                        </List.Item>
                      ))}
                    </List>
                  </div>
                ) : null}
                {applicationModalOrder.applicationStatus === 'PARTIALLY_APPLIED' ? (
                  <Message info content="Algunos productos pueden tener el stock actualizado. Para evitar descontarlo dos veces, no podes volver a intentar este pedido completo." />
                ) : null}
                {applicationRetryError ? <Message negative content={applicationRetryError} /> : null}
                {!canRetry ? (
                  <div style={{ ...mutedTextStyle, fontSize: 12 }}>
                    El reintento no esta disponible para el estado actual de este pedido.
                  </div>
                ) : null}
              </div>
            );
          })() : null}
        </Modal.Content>
        <Modal.Actions>
          <Button disabled={applicationRetrying} onClick={() => setApplicationModalOrder(null)}>
            Cerrar
          </Button>
          {canRetryOrderApplication(applicationModalOrder) ? (
              <Button
                primary
                loading={applicationRetrying}
                disabled={applicationRetrying}
                onClick={retryApplication}
              >
                Volver a intentar
              </Button>
            ) : null}
        </Modal.Actions>
      </Modal>

      <Modal open={!!detailModalOrder} onClose={() => setDetailModalOrder(null)} size="small" closeIcon>
        <Modal.Header>Detalle de la orden</Modal.Header>
        <Modal.Content>
          {detailModalOrder ? (
            <OrderDetailContent
              order={detailModalOrder}
              formatMoneyARS={formatMoneyARS}
              formatDateTime={formatDateTime}
            />
          ) : (
            <div>Sin datos</div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setDetailModalOrder(null)}>Cerrar</Button>
        </Modal.Actions>
      </Modal>

      <Modal open={bulkPriceUpdateEnabled === true && !!financialModalOrderId} onClose={() => setFinancialModalOrderId(null)} size="small" closeIcon>
        <Modal.Header>Rentabilidad por orden</Modal.Header>
        <Modal.Content>
          {financialModalOrderId && orderFinancialsByOrderId[financialModalOrderId] ? (
            <OrderFinancialDetail
              order={rows.find((row) => row.id === financialModalOrderId)}
              snapshot={orderFinancialsByOrderId[financialModalOrderId]}
              formatMoneyARS={formatMoneyARS}
              formatDateTime={formatDateTime}
            />
          ) : (
            <div>Sin datos</div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setFinancialModalOrderId(null)}>Cerrar</Button>
        </Modal.Actions>
      </Modal>
    </Container>
  );
};

export default Orders;

const compactListStyle = {
  margin: 0,
  padding: 16,
  borderRadius: 16,
  border: '1px solid #E5E7EB',
  background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
};

const getAddressLine = (address) => {
  if (!address) return 'No disponible';
  const street = [address.streetName, address.streetNumber].filter(Boolean).join(' ');
  const city = [address.locality, address.city, address.state].filter(Boolean).join(', ');
  const country = [address.country, address.zipCode].filter(Boolean).join(' ');
  return [street, city, country].filter(Boolean).join(' - ') || 'No disponible';
};

const getBuyerLabel = (buyer) => {
  if (!buyer) return 'No disponible';
  return buyer.displayName || buyer.nickname || buyer.email || buyer.id || 'No disponible';
};

const OrderDetailContent = ({ order, formatMoneyARS, formatDateTime }) => {
  const buyer = order.buyer || {};
  const sellerLabel = order.sellerLabel || order.sellerId || 'No disponible';
  const finalAmount = order.billing?.finalChargedAmount ?? order.chargedAmount ?? order.paidAmount ?? order.totalAmount;

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <List relaxed style={compactListStyle}>
        <List.Item>Orden: <b>{order.marketplaceId}</b></List.Item>
        <List.Item>Cuenta vendedora: <b>{sellerLabel}</b></List.Item>
        <List.Item>Estado: <b>{order.status || 'No disponible'}</b></List.Item>
        <List.Item>Fecha: <b>{formatDateTime(order.orderDate) || 'No disponible'}</b></List.Item>
      </List>

      <List relaxed style={compactListStyle}>
        <List.Item>Comprador: <b>{getBuyerLabel(buyer)}</b></List.Item>
        <List.Item>Nombre: <b>{[buyer.firstName, buyer.lastName].filter(Boolean).join(' ') || 'No disponible'}</b></List.Item>
        <List.Item>Email: <b>{buyer.email || 'No disponible'}</b></List.Item>
      </List>

      <List relaxed style={compactListStyle}>
        <List.Item>Direccion de envio: <b>{getAddressLine(order.shippingAddress)}</b></List.Item>
        {order.shippingAddress?.phone ? (
          <List.Item>Telefono: <b>{order.shippingAddress.phone}</b></List.Item>
        ) : null}
        {order.shippingAddress?.comment ? (
          <List.Item>Comentario: <b>{order.shippingAddress.comment}</b></List.Item>
        ) : null}
      </List>

      <List relaxed style={compactListStyle}>
        <List.Item>Total venta: <b>{formatMoneyARS(order.totalAmount) || 'No disponible'}</b></List.Item>
        <List.Item>Total pagado/cobrado: <b>{formatMoneyARS(finalAmount) || 'No disponible'}</b></List.Item>
      </List>
    </div>
  );
};

const hasSnapshotInternalCost = (snapshot) => snapshot?.hasInternalCost === true;

const renderFinancialAmount = (snapshot, amount, formatMoneyARS, options = {}) => {
  const { pendingWhenZero = false } = options;
  if (pendingWhenZero && snapshot?.status === 'PENDING' && Number(amount || 0) <= 0) {
    return 'Pendiente';
  }
  return formatMoneyARS(amount);
};

const OrderFinancialDetail = ({ order, snapshot, formatMoneyARS, formatDateTime }) => {
  const hasShippingBreakdown =
    (snapshot.shippingGrossChargeAmount !== null && snapshot.shippingGrossChargeAmount !== undefined) ||
    (snapshot.buyerShippingAmount !== null && snapshot.buyerShippingAmount !== undefined) ||
    (snapshot.sellerShippingNetAmount !== null && snapshot.sellerShippingNetAmount !== undefined);
  const sourceLabel =
    snapshot.mode === 'TN_COST_ONLY'
      ? 'Tienda Nube costo-only'
      : snapshot.mode === 'ML_ESTIMATED' && snapshot.status === 'PENDING'
        ? 'Mercado Libre en consulta'
        : snapshot.status === 'PARTIAL'
          ? 'Mercado Libre parcial'
          : snapshot.status === 'UNAUTHORIZED'
            ? 'Permiso ML faltante'
            : 'Pendiente';

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {sourceLabel !== 'Pendiente' ? (
          <Label basic style={{ borderRadius: 999, padding: '8px 12px', fontWeight: 700 }}>
            {sourceLabel}
          </Label>
        ) : null}
      </div>
      {snapshot.lastError ? <Message warning content={snapshot.lastError} /> : null}
      {snapshot.mode === 'ML_ESTIMATED' && snapshot.status === 'PENDING' ? (
        <Message
          info
          content="Stocky esta consultando cargos reales de Mercado Libre. Mientras tanto se muestran solo los importes ya conocidos."
        />
      ) : null}
      {order ? (
        <OrderDetailContent
          order={order}
          formatMoneyARS={formatMoneyARS}
          formatDateTime={formatDateTime}
        />
      ) : null}
      <List
        relaxed
        style={{
          margin: 0,
          padding: 16,
          borderRadius: 16,
          border: '1px solid #E5E7EB',
          background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
        }}
      >
        <List.Item>Venta: <b>{formatMoneyARS(snapshot.revenueAmount)}</b></List.Item>
        <List.Item>
          Costo interno: <b>{hasSnapshotInternalCost(snapshot) ? formatMoneyARS(snapshot.costAmount) : 'No cargado'}</b>
        </List.Item>
        <List.Item>
          Fee marketplace: <b>{renderFinancialAmount(snapshot, snapshot.marketplaceFeeAmount, formatMoneyARS, { pendingWhenZero: snapshot.mode === 'ML_ESTIMATED' })}</b>
        </List.Item>
        <List.Item>
          Financiacion: <b>{renderFinancialAmount(snapshot, snapshot.financingFeeAmount, formatMoneyARS, { pendingWhenZero: snapshot.mode === 'ML_ESTIMATED' })}</b>
        </List.Item>
        {hasShippingBreakdown ? (
          <>
            <List.Item>
              Cargo bruto de envio: <b>{formatMoneyARS(snapshot.shippingGrossChargeAmount)}</b>
            </List.Item>
            <List.Item>
              Envio pagado por comprador: <b>{formatMoneyARS(snapshot.buyerShippingAmount)}</b>
            </List.Item>
            <List.Item>
              Costo neto de envio: <b>{renderFinancialAmount(snapshot, snapshot.sellerShippingNetAmount, formatMoneyARS, { pendingWhenZero: snapshot.mode === 'ML_ESTIMATED' })}</b>
            </List.Item>
          </>
        ) : (
          <List.Item>
            Envio: <b>{renderFinancialAmount(snapshot, snapshot.shippingCostAmount, formatMoneyARS, { pendingWhenZero: snapshot.mode === 'ML_ESTIMATED' })}</b>
          </List.Item>
        )}
        <List.Item>
          Impuestos: <b>{renderFinancialAmount(snapshot, snapshot.taxAmount, formatMoneyARS, { pendingWhenZero: snapshot.mode === 'ML_ESTIMATED' })}</b>
        </List.Item>
        <List.Item>Otros cargos: <b>{formatMoneyARS(snapshot.otherChargesAmount)}</b></List.Item>
        <List.Item>Neto a recibir: <b>{formatMoneyARS(snapshot.netPayoutAmount)}</b></List.Item>
        <List.Item>
          Margen: <b>{hasSnapshotInternalCost(snapshot) ? formatMoneyARS(snapshot.marginAmount) : 'No disponible sin costo interno'}</b>
        </List.Item>
      </List>
      <div style={{ color: '#666' }}>Calculado: {formatDateTime(snapshot.calculatedAt)}</div>
    </div>
  );
};
